export type Query = Record<string, string | number | boolean | undefined>;

export interface ClientOptions {
  baseUrl: string; // should include /api
}

export interface RequestOptions {
  method?: "GET" | "POST" | "PATCH" | "PUT" | "DELETE";
  headers?: Record<string, string>;
  query?: Query;
  body?: unknown;
  keepalive?: boolean;
  token?: string | null;
}

function buildQuery(query?: Query): string {
  if (!query) return "";
  const params = Object.entries(query)
    .filter(([, v]) => v !== undefined && v !== "")
    .map(
      ([k, v]) => `${encodeURIComponent(k)}=${encodeURIComponent(String(v))}`,
    )
    .join("&");
  return params ? `?${params}` : "";
}

async function request<T>(
  baseUrl: string,
  path: string,
  opts: RequestOptions = {},
): Promise<T> {
  const headers: Record<string, string> = { ...(opts.headers || {}) };
  // Set Content-Type only when we actually send a body to avoid unnecessary preflights
  if (opts.body !== undefined) {
    headers["Content-Type"] = "application/json";
  }
  if (opts.token) headers.Authorization = `Bearer ${opts.token}`;

  const url = `${baseUrl}${path}${buildQuery(opts.query)}`;

  try {
    // Step 1: Use fetch to get data
    const controller = new AbortController();
    const timeoutId = setTimeout(() => {
      controller.abort();
    }, 10000);

    const response = await fetch(url, {
      method: opts.method || "GET",
      headers,
      body: opts.body ? JSON.stringify(opts.body) : undefined,
      signal: controller.signal,
      keepalive: opts.keepalive,
    });

    clearTimeout(timeoutId);

    // Step 2: Parse data immediately
    let data: T;
    if (response.status === 204) {
      data = undefined as unknown as T;
    } else if (response.status >= 400) {
      const contentType = response.headers.get("content-type");

      if (contentType?.includes("application/json")) {
        const errorData = await response.json();

        // Extract human-readable message from error response
        const errorMessage =
          errorData.error || errorData.message || "An error occurred";
        const errorCode = errorData.code;

        // Create error object with structured data
        const error = new Error(errorMessage) as Error & {
          code?: string;
          status: number;
          waitSeconds?: number;
          limitStatus?: unknown;
          payload?: unknown;
        };
        error.status = response.status;
        if (errorCode) error.code = errorCode;
        if (errorData.waitSeconds) error.waitSeconds = errorData.waitSeconds;
        if (errorData.limitStatus) error.limitStatus = errorData.limitStatus;
        error.payload = errorData;

        throw error;
      } else {
        const errorText = await response.text();
        throw new Error(
          errorText || `Request failed with status ${response.status}`,
        );
      }
    } else {
      data = (await response.json()) as T;
    }

    // Step 3: Return the data directly
    return data;
  } catch (err) {
    // Handle AbortError specifically
    if (err instanceof Error && err.name === "AbortError") {
      throw new Error(`Request timeout after 30 seconds: ${url}`);
    }

    throw err;
  }
}

export { request };
